import { useEffect } from "react";
import { useState, useCallback } from "react";
import { t } from "i18next";
import { Pagination } from "../../components/pagination/Pagination";
import ServicesTable from "./sections/ServicesTable";

import "./Services.scss";
import ServicesFilterForm from "./filter/ServicesFilterForm";
import FilterDrawer from "../../components/filter-drawer/FilterDrawer";
import {
    SearchOptions,
    SearchQuery,
    createTextSearchQuery,
    searchWithFilters,
} from "../../services/api/search";
import FiltersDisplay from "../../components/filter/FiltersDisplay";
import { GenericListLayout } from "../../components/generic-list-view/GenericListLayout";
import EditCreateDrawer from "../../components/edit-create-drawer/EditCreateDrawer";
import ServiceForm, { ServiceFormValidator } from "../../components/data/service/ServiceForm";
import { validator } from "../../services/form-validator/form-validator";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../services/api/error";
import { api } from "../../services/api/api";
import { isOdooConnected } from "../../services/odoo/odoo";
import { useTablePageSize } from "../../hooks/useTablePageSize";

export function Services() {
    const isNotOdooConnected = !isOdooConnected();

    const [servicesPageState, setServicesPageState] = useState<EasyTech.Service[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [pageNumberState, setPageNumberState] = useState<number>(1);
    const [pageState, setPageState] = useState<number>(0);
    const [orderState, setOrderState] = useState<{ head: string, orderDesc: boolean }>({ head: "label", orderDesc: false });
    const [filters, setFilters] = useState<Partial<EasyTech.Service>>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const pageSize = useTablePageSize();

    /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
     * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
     * une page vide. */
    useEffect(() => { setPageState(0); }, [pageSize]);

    const onOrderChange = (head: string, orderDesc: boolean) => {
        setOrderState({ head, orderDesc });
    };

    const loadServicesWithFilters = useCallback(async () => {
        setIsLoading(true);
        try {
            const queries: SearchQuery[] = [];

            if (filters.label) {
                queries.push(createTextSearchQuery(["label"], filters.label));
            }

            if (filters.ref) {
                queries.push(createTextSearchQuery(["ref"], filters.ref));
            }

            if (filters.label && filters.ref && filters.label === filters.ref) {
                queries.pop();
                queries.pop();
                queries.push(createTextSearchQuery(["label", "ref"], filters.label));
            }

            if (filters.type) {
                queries.push(createTextSearchQuery(["type"], filters.type));
            }

            const searchOptions: SearchOptions = {
                paginate: {
                    pageSize,
                    pageNumber: pageState,
                },
                order: {
                    by: orderState.head,
                    direction: orderState.orderDesc ? "DESC" : "ASC",
                },
                queries,
            };

            const response = await searchWithFilters<EasyTech.Service>(
                "services",
                searchOptions
            );

            if (response && response.data) {
                setServicesPageState(response.data);
                setTotalItems(response.data.length);
                setPageNumberState(response.totalPages);
            } else {
                setServicesPageState([]);
                setPageNumberState(1);
            }
        } catch (error) {
            console.error("Error fetching services:", error);
            setServicesPageState([]);
            setPageNumberState(1);
        } finally {
            setIsLoading(false);
        }
    }, [orderState, filters, pageState, pageSize]);


    useEffect(() => {
        loadServicesWithFilters();
    }, [loadServicesWithFilters]);

    const createService = (data: EasyTech.Service): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (validator.isFormValid(data, ServiceFormValidator)) {
                api.post("users/services", data).then((ret) => {
                    toast.success("Service created !");
                    return resolve();
                }).catch((err) => {
                    showApiErrorToast(err);
                    return reject(err);
                });
            } else {
                const fieldNames = {
                    category: t("common:category"),
                    label: t("common:label"),
                    price: t("common:sellingPrice"),
                    ref: t("common:ref:label"),
                    type: t("common:type")
                };

                const errorMessage = validator.getFormErrorMessage(data, ServiceFormValidator, fieldNames, t);
                toast.error(errorMessage);
                return reject();
            }
        });
    };

    const handleFilterSubmit = (
        filterData: Partial<EasyTech.Service>
    ): Promise<void> => {
        return new Promise<void>((resolve) => {
            setFilters(filterData || {});
            setPageState(0);
            resolve();
        });
    };

    const clearFilter = (field: string): Promise<void> => {
        return new Promise<void>((resolve) => {
            const newFilters = { ...filters };
            delete newFilters[field as keyof Partial<EasyTech.Service>];
            setFilters(newFilters);
            setPageState(0);
            resolve();
        });
    };

    const clearAllFilters = (): Promise<void> => {
        return new Promise<void>((resolve) => {
            setFilters({});
            setPageState(0);
            resolve();
        });
    };

    const headerAction = (
        <EditCreateDrawer<EasyTech.Service>
            entityName="Service"
            FormComponent={ServiceForm}
            onSubmit={createService}
            isNotEditable
        />
    );

    const filterDrawerComponent = (
        <FilterDrawer<EasyTech.Service>
            FormComponent={ServicesFilterForm}
            onSubmit={handleFilterSubmit}
            entityName="Services"
        />
    );

    const filterBadgesComponent = (
        <FiltersDisplay
            filters={filters}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
            size="sm"
            compact={true}
            entity="service"
        />
    );

    const mainContent = servicesPageState && servicesPageState.length > 0 ? (
        <ServicesTable services={servicesPageState} onHeadClick={onOrderChange}
            selectedHead={orderState.head} orderDesc={orderState.orderDesc}
            onServiceUpdated={loadServicesWithFilters} />
    ) : (
        <div className="text-center py-8 bg-blue-gray-50 rounded-lg">
            <p className="text-lg text-blue-gray-700">
                {t("common:noData")}
            </p>
            <p className="text-sm text-blue-gray-500 mt-2">
                {Object.keys(filters).length > 0
                    ? t("common:noDataFilters")
                    : t("common:noDataAdd")}
            </p>
        </div>
    );

    const paginationComponent = totalItems > 0 ? (
        <Pagination
            actualPage={pageState}
            pageNumber={pageNumberState}
            changePage={setPageState}
        />
    ) : null;

    return (
        <GenericListLayout
            entityName={t("common:services:label")}
            path="/services"
            isLoading={isLoading}
            headerActions={isNotOdooConnected ? headerAction : null}
            filterDrawer={filterDrawerComponent}
            filterBadges={filterBadgesComponent}
            content={mainContent}
            pagination={paginationComponent}
            className="services"
        />
    );
}
